import React, { useMemo } from "react";
import { CollectionSelector } from "../../../media-library/components/collection-selector";
import { CollectionColor } from "../../../media-library/components/collection-color";
import { MediaCollection } from "../../../../api/media-library";
import { entitlementsPermission } from "../../../../utils/entitlements-permission";
import { getUser as getCurrentUser } from "../../../../selectors/commonSelectors";
import { getSelectedCollection } from "../../../../selectors/mediaLibrarySelectors";
import { useSelector } from "react-redux";
import {
  MultiSelect,
  BaseOption,
} from "../../../../components/ui/multi-select";

interface CollectionOption extends BaseOption {
  color?: string;
}

export const CollectionSettings = ({
  collectionUserCanAccess,
  setCollectionUserCanAccess,
  collections,
}: {
  collectionUserCanAccess: MediaCollection | string[] | null;
  setCollectionUserCanAccess: (
    newValue: MediaCollection | string[] | null
  ) => void;
  collections?: MediaCollection[];
}): React.ReactElement => {
  const currentUser = useSelector(getCurrentUser);
  // With no restriction set, the picker starts on the last collection viewed in the library.
  const lastViewedCollection = useSelector(getSelectedCollection);

  // Entitlement-driven (user-permissions=advanced): per-collection access
  // restriction, previously Enterprise-only, now available to Accelerate+.
  const canRestrictCollections =
    entitlementsPermission.hasAdvancedUserPermissions(currentUser);

  const options = useMemo(() => {
    return (collections || []).map((collection) => ({
      value: collection._id,
      label: collection.name,
      color: collection?.preferences?.color ?? undefined,
    })) as CollectionOption[];
  }, [collections]);

  const selectedOptions = useMemo(() => {
    if (!collectionUserCanAccess || !canRestrictCollections) return [];
    const selectedIds = collectionUserCanAccess as string[];
    return options.filter((c) => selectedIds.includes(c.value));
  }, [options, collectionUserCanAccess, canRestrictCollections]);

  return (
    <div>
      <p>
        <strong>
          Collection that this user can <em>only</em> create, view, update and
          remove assets from:
        </strong>
      </p>
      {canRestrictCollections ? (
        <MultiSelect
          options={options}
          value={selectedOptions}
          onChange={(selectedOptions) => {
            setCollectionUserCanAccess(selectedOptions.map((c) => c.value));
          }}
          placeholder="Select a collection…"
          emptyMessage="No other collections"
          searchable={true}
          closeMenuOnSelect={false}
          renderOption={(option) => (
            <span className="flex items-center gap-2.5">
              <CollectionColor
                collection={{
                  preferences: option.color
                    ? { color: option.color }
                    : undefined,
                }}
                className="shrink-0"
              />
              {option.label}
            </span>
          )}
        />
      ) : (
        <>
          <CollectionSelector
            className="md:w-full"
            selectedCollection={
              (collectionUserCanAccess as MediaCollection | null) ??
              lastViewedCollection ??
              null
            }
            collections={collections || []}
            onChange={(id) => {
              setCollectionUserCanAccess(
                collections?.find((collection: any) => collection._id === id) ??
                  null
              );
            }}
          />
          {!canRestrictCollections && (
            <div style={{ marginTop: 10 }}>
              Upgrade to Accelerate or above to enable multi-collection access
              for users.
            </div>
          )}
        </>
      )}
    </div>
  );
};
